import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, getDocFromServer, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [profile, setProfile] = useState(undefined);
  const [proProfile, setProProfile] = useState(undefined);
  const [profileLoading, setProfileLoading] = useState(true);

  const fetchProfile = useCallback(async (uid, { fromServer = false } = {}) => {
    try {
      const ref = doc(db, 'users', uid);
      const snap = fromServer ? await getDocFromServer(ref) : await getDoc(ref);
      const data = snap.exists() ? snap.data() : null;
      const hasName = !!(data?.first_name || data?.full_name || data?.display_name || data?.name);
      if (import.meta.env.DEV) {
        console.log('=== AUTH DEBUG ===');
        console.log('UID:', uid);
        console.log('Doc exists:', snap.exists());
        console.log('Doc data:', JSON.stringify(data));
        console.log('Current redirect logic result (needsOnboarding):', !data || !hasName);
        console.log('  !data:', !data);
        console.log('  first_name:', data?.first_name);
        console.log('  full_name:', data?.full_name);
        console.log('  display_name:', data?.display_name);
        console.log('  name:', data?.name);
        console.log('  hasName:', hasName);
        console.log('==================');
      }
      setProfile(data);

      // If pro, also fetch professionals doc in parallel
      if (data?.role === 'pro') {
        const proRef = doc(db, 'professionals', uid);
        const proSnap = fromServer ? await getDocFromServer(proRef) : await getDoc(proRef);
        setProProfile(proSnap.exists() ? proSnap.data() : null);
      } else {
        setProProfile(null);
      }
    } catch (err) {
      setProfile(null);
      setProProfile(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setProfileLoading(true);
        setProfile(undefined);
        setProProfile(undefined);

        // Gate B: For Google/social sign-ins, create a minimal doc only if none exists.
        // This prevents overwriting an existing profile on re-sign-in.
        const socialProviders = (currentUser.providerData || []).filter(
          (p) => p.providerId !== 'password'
        );
        if (socialProviders.length > 0) {
          try {
            const userRef = doc(db, 'users', currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) {
              await setDoc(userRef, {
                uid: currentUser.uid,
                email: currentUser.email || '',
                role: null,
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
              });
            }
          } catch (err) {
            console.error('AuthContext users-doc guard failed:', err);
          }
        }

        fetchProfile(currentUser.uid);
      } else {
        setProfile(null);
        setProProfile(null);
        setProfileLoading(false);
      }
    });
    return () => unsubscribe();
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      setProfileLoading(true);
      await fetchProfile(user.uid, { fromServer: true });
    }
  }, [user, fetchProfile]);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    setProfile(null);
    setProProfile(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, proProfile, profileLoading, refreshProfile, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
